import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FolderKanban, FileWarning, Activity as ActivityIcon,
  Settings, LogOut, ChevronRight, Users as UsersIcon, Building2, User,
  Menu, X, LifeBuoy,
  Inbox, ClipboardList, Share2, Package, LayoutDashboard, Lightbulb,
  UploadCloud, Search, BookCheck, Hammer, Star,
} from "lucide-react";
import Logo from "@/components/Logo";
import NotificationsBell from "@/components/NotificationsBell";
import SyncBadge from "@/components/SyncBadge";
import { SearchTrigger } from "@/components/search/CommandPalette";
import FeedbackTrigger from "@/components/FeedbackTrigger";
import { ThemeToggle } from "@/components/ThemeToggle";
import AccountSettingsModal from "@/components/account/AccountSettingsModal";
import WhatsNewToast from "@/components/WhatsNewToast";
import EntitlementStatusBanner from "@/components/EntitlementStatusBanner";
import UnsavedDraftsBanner from "@/components/UnsavedDraftsBanner";
import LegalFooter from "@/components/legal/LegalFooter";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { ROLE_LABEL } from "@/lib/utils";

const NAV = [
  { to: "/portfolio",  label: "Projects",         icon: FolderKanban,    testid: "nav-projects" },
  { to: "/my-sheets",  label: "My Sheets",        icon: Star,            testid: "nav-my-sheets" },
  { to: "/ask",        label: "Ask",              icon: Search,          testid: "nav-ask" },
  { to: "/knowledge",  label: "Knowledge",        icon: BookCheck,       testid: "nav-knowledge" },
  { to: "/inbox",      label: "Inbox",            icon: Inbox,           testid: "nav-inbox" },
  { to: "/field-roll", label: "Field Roll",       icon: Hammer,          testid: "nav-field-roll" },
  { to: "/intake",     label: "Documents",        icon: UploadCloud,     testid: "nav-documents" },
  { to: "/punch",      label: "Field Findings",   icon: ClipboardList,   testid: "nav-field-findings" },
  { to: "/shares",     label: "Shares",           icon: Share2,          testid: "nav-shares" },
  { to: "/exports",    label: "Exports",          icon: Package,         testid: "nav-exports" },
];

// Iter 109 P0 — Admin block split:
//   ADMIN_NAV_OWNERS = owner/admin only (Issues / Audit Trail)
//   `Operations` and `Field Findings` are gated above via canSeeOperations / canSeeFindings
const ADMIN_NAV = [
  { to: "/issues", label: "Issues", icon: FileWarning, testid: "nav-issues" },
  { to: "/activity", label: "Audit Trail", icon: ActivityIcon, testid: "nav-activity" },
];

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const [accountOpen, setAccountOpen] = useState(false);
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  // Iter 71.3 — mobile sidebar drawer state.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change (NavLink click on mobile).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close drawer on Escape key.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Lock body scroll while drawer open.
  useEffect(() => {
    if (mobileOpen) {
      const orig = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = orig; };
    }
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isAdmin = user && ["admin", "owner"].includes(user.role);
  // Iter 109 P0 — Operations rollup + Field Findings must reach the actual
  // construction cohorts (PM, Engineer, Ops Mgr, Exec, Super, Foreman, Inspector).
  // The Bucket-A "Platform Console" link remains platform-admin-only.
  const canSeeOperations = user && [
    "owner","admin","executive","operations_manager",
    "project_manager","project_engineer",
  ].includes(user.role);
  const canSeeFindings = user && [
    "owner","admin","executive","operations_manager",
    "project_manager","project_engineer",
    "superintendent","foreman","inspector",
  ].includes(user.role);

  return (
    <div className="min-h-screen flex" data-testid="app-shell">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40 transition-opacity"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — fixed drawer on mobile, in-flow column on >= md */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-[260px] md:w-[240px] shrink-0 border-r border-[var(--fop-border)] bg-[var(--fop-surface)] flex flex-col transform transition-transform duration-200 ease-out md:transform-none ${
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        data-testid="app-sidebar"
        aria-hidden={!mobileOpen ? "true" : undefined}
      >
        <div className="px-4 py-4 border-b border-[var(--fop-border)] flex items-center justify-between gap-2">
          <Logo size="md" data-testid="sidebar-logo" />
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden w-11 h-11 inline-flex items-center justify-center rounded-md text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
            aria-label="Close menu"
            data-testid="sidebar-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                    : "text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={16} style={{ color: isActive ? "var(--fop-primary)" : undefined }} />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight size={14} className="ml-auto text-[var(--fop-text-3)]" />}
                </>
              )}
            </NavLink>
          ))}

          {/* Iter 109 P0 — Operations rollup visible to all customer ops roles */}
          {canSeeOperations && (
            <NavLink
              to="/operations"
              data-testid="nav-operations-rollup"
              className={({ isActive }) =>
                `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                    : "text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Building2 size={16} style={{ color: isActive ? "var(--fop-primary)" : undefined }} />
                  <span>Daily Ops</span>
                  {isActive && <ChevronRight size={14} className="ml-auto text-[var(--fop-text-3)]" />}
                </>
              )}
            </NavLink>
          )}

          {/* Iter 109 P0 — Field Findings unlocked for the field cohort
              (Iter 124 — duplicate of /punch sidebar entry retired; primary
              `nav-field-findings` link in NAV[] now points at /punch which
              is the Field Findings list). */}
          {false && canSeeFindings && (
            <NavLink
              to="/discoveries"
              data-testid="nav-discoveries"
              className={({ isActive }) =>
                `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                    : "text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Lightbulb size={16} style={{ color: isActive ? "var(--fop-primary)" : undefined }} />
                  <span>Field Findings</span>
                  {isActive && <ChevronRight size={14} className="ml-auto text-[var(--fop-text-3)]" />}
                </>
              )}
            </NavLink>
          )}

          {isAdmin && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-4)] font-mono">
                Admin
              </div>
              {ADMIN_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                        : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon size={14} style={{ color: isActive ? "var(--fop-primary)" : undefined }} />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
              <NavLink
                to="/settings"
                data-testid="nav-settings"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                      : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                  }`
                }
              >
                <Settings size={14} />
                <span>Company Settings</span>
              </NavLink>
              {/* Iter 55 — User management (owner/admin) */}
              <NavLink
                to="/admin/users"
                data-testid="nav-tenant-admin"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                      : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                  }`
                }
              >
                <UsersIcon size={14} />
                <span>Users</span>
              </NavLink>
              {/* Iter 109 — Field Findings moved out of admin block; visible to field cohort. Documents lives in main nav (Iter 103). */}
            </>
          )}
          {user?.is_platform_admin && (
            <NavLink
              to="/platform"
              data-testid="nav-platform-admin"
              title="ForgedOps Operations Console"
              className={({ isActive }) =>
                `flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)] border border-[var(--fop-border-2)]"
                    : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border border-transparent"
                }`
              }
            >
              <Building2 size={14} />
              <span>Platform Console</span>
            </NavLink>
          )}
        </nav>

        {/* User block */}
        <div className="border-t border-[var(--fop-border)] p-3">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold font-display"
              style={{
                background: "linear-gradient(135deg, var(--fop-primary), var(--fop-accent))",
                color: "var(--fop-bg)",
              }}
              data-testid="user-avatar"
            >
              {(user?.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[var(--fop-text)] truncate" data-testid="user-name">
                {user?.name}
              </div>
              <div className="text-[10px] uppercase font-mono tracking-wider text-[var(--fop-text-3)]" data-testid="user-role">
                {ROLE_LABEL[user?.role] || user?.role}
              </div>
            </div>
          </div>
          <button
            onClick={() => setAccountOpen(true)}
            className="btn btn-ghost w-full mt-1 justify-start text-xs"
            data-testid="account-settings-btn"
          >
            <User size={14} />
            Account
          </button>
          <button
            onClick={handleLogout}
            className="btn btn-ghost w-full mt-1 justify-start text-xs"
            data-testid="logout-btn"
          >
            <LogOut size={14} />
            Sign out
          </button>
          <div className="mt-2">
            <FeedbackTrigger />
          </div>
          {/* PILLAR 4 — Daylight / Field Black toggle. */}
          <div className="mt-2">
            <ThemeToggle />
          </div>
          <NavLink
            to="/help"
            className={({ isActive }) =>
              `mt-2 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors ${
                isActive ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              }`
            }
            data-testid="sidebar-help-link"
            title="Getting started guide"
          >
            <LifeBuoy size={13} /> Getting started
          </NavLink>
          <NavLink
            to="/support"
            className={({ isActive }) =>
              `mt-2 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors ${
                isActive ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              }`
            }
            data-testid="sidebar-support-link"
            title="Need help? · support@forgedopshq.com"
          >
            <LifeBuoy size={13} /> Need help?
          </NavLink>
          {tenant?.branding?.powered_by_forgedops && (
            <div className="mt-3 text-[9px] uppercase tracking-[0.22em] text-[var(--fop-text-4)] font-mono text-center">
              {tenant?.branding?.footer_text || "Powered by ForgedOps™"}
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div className="h-1 industrial-stripe" />
        {user?.is_platform_admin && (
          <div className="bg-[#7c2d12] text-amber-100 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] flex flex-wrap items-center justify-between gap-x-3 gap-y-1 min-w-0" data-testid="platform-mode-banner">
            <div className="inline-flex items-center gap-2 min-w-0 max-w-full">
              <span className="shrink-0 px-1.5 py-0.5 bg-amber-400 text-black rounded text-[9px]">PLATFORM MODE</span>
              <span className="truncate">ForgedOps Operations · {user?.email}</span>
            </div>
            <div className="hidden sm:inline-flex flex-wrap items-center gap-x-3 gap-y-1 opacity-90 min-w-0 max-w-full">
              <span data-testid="banner-env">env: {(process.env.REACT_APP_ENVIRONMENT || (window.location.hostname.includes("preview") ? "PREVIEW" : (window.location.hostname.includes("forgedopsplans.com") ? "PRODUCTION" : "LOCAL")))}</span>
              <NavLink to="/platform" className="underline hover:text-white" data-testid="banner-platform-link">/platform</NavLink>
              <NavLink to={{ pathname: "/platform", search: "?tab=onboard" }} className="underline hover:text-white" data-testid="banner-onboard-link">+ onboard</NavLink>
              <NavLink to="/portfolio" className="underline hover:text-white" data-testid="banner-portfolio-link">tenant view</NavLink>
            </div>
          </div>
        )}
        <EntitlementStatusBanner />
        <UnsavedDraftsBanner />
        <div className="h-14 border-b border-[var(--fop-border)] bg-[var(--fop-surface)] flex items-center px-3 sm:px-4 gap-2 sm:gap-3" data-testid="app-header">
          {/* Iter 71.3 — hamburger toggle (mobile only) */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden w-11 h-11 inline-flex items-center justify-center rounded-md text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
            data-testid="sidebar-open-btn"
          >
            <Menu size={20} />
          </button>
          <div className="md:hidden flex-1 min-w-0">
            <Logo size="sm" data-testid="topbar-logo-mobile" />
          </div>
          <div className="hidden md:block flex-1" />
          <SearchTrigger />
          <SyncBadge />
          <NotificationsBell />
        </div>
        <div className="flex-1 overflow-auto" data-scroll-root>{children}</div>
        <LegalFooter variant="compact" />
      </main>
      <AccountSettingsModal open={accountOpen} onClose={() => setAccountOpen(false)} />
      <WhatsNewToast />
    </div>
  );
}
